import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { GET_COMPREHENSIVE_ANALYTICS } from '../graphql/queries/getComprehensiveAnalytics';

interface UseGetComprehensiveAnalyticsParams {
  communityId: string;
  fromDate: string;
  toDate: string;
  skip?: boolean;
}

/**
 * Comprehensive analytics data returned from the GraphQL query
 * This interface matches the GET_COMPREHENSIVE_ANALYTICS GraphQL query response
 */
export interface ComprehensiveAnalytics {
  lastAuditDate?: string | null;
  confidence?: string | null;
  totalReports: number;
  auditSessions: number;
  itemsAudited: number;
  performanceMetrics: {
    avgAccuracy?: number | null;
    avgPrecision?: number | null;
    avgRecall?: number | null;
    avgF1Score?: number | null;
  };
  labelAnalysis: {
    exactMatches: { count: number; percentage: number };
    missingLabels: { count: number; percentage: number };
    additionalLabels: { count: number; percentage: number };
    bothMissingAndAdditional: { count: number; percentage: number };
  };
  decisionAnalysis?: {
    conflicts: { count: number; percentage: number };
    skipped: { count: number; percentage: number };
  } | null;
}

export function useGetComprehensiveAnalytics({ communityId, fromDate, toDate, skip = false }: UseGetComprehensiveAnalyticsParams) {
  const { data, loading, error, refetch } = useQuery(GET_COMPREHENSIVE_ANALYTICS, {
    variables: {
      communityId,
      fromDate,
      toDate,
    },
    skip,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const comprehensiveAnalytics = useMemo(() => data?.community?.comprehensiveAnalytics || null, [data]);

  return {
    comprehensiveAnalytics,
    isLoading: loading,
    error,
    refetch,
  };
}
